import { sortBy } from "lodash";

import { getDate, getIsoDate } from "./formatters";
import { addDaysToIsoDate, getAllDaysInDateRange } from "./get_cycle_weeks";

interface DatedData<T> {
  date: string;
  data: T;
}

export enum Weekday {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export function getMostRecentPastDay(
  referenceDate: Date,
  weekStartDay: Weekday,
): string {
  const dayOfWeek: Weekday = referenceDate.getDay();
  if (dayOfWeek === weekStartDay) {
    return getIsoDate(referenceDate);
  }

  if (dayOfWeek > weekStartDay) {
    const daysToGoBack = dayOfWeek - weekStartDay;
    const resDate = new Date(referenceDate);
    resDate.setDate(resDate.getDate() - daysToGoBack);
    return getIsoDate(resDate);
  }

  const daysToGoBack = dayOfWeek - weekStartDay + 7;
  const resDate = new Date(referenceDate);
  resDate.setDate(resDate.getDate() - daysToGoBack);
  return getIsoDate(resDate);
}

export function rangedToDailyData<T>(
  rangedData: DatedData<T>,
  daysPerData: number,
  dataTransformer: (datum: T, date: string) => T,
) {
  const startDate = rangedData.date;
  const endDate = addDaysToIsoDate(startDate, daysPerData - 1);

  const allDays = getAllDaysInDateRange({
    start_date: startDate,
    end_date: endDate,
  });

  return allDays.map((date) => ({
    date,
    data: dataTransformer(rangedData.data, date),
  }));
}

export function weeklyToDailyData<T>(
  weeklyData: DatedData<T>[],
  dataTransformer: (data: T, date: string) => T,
) {
  return weeklyData.flatMap((weeklyDatum) =>
    rangedToDailyData(weeklyDatum, 7, dataTransformer),
  );
}

export function dailyToRangedData<T>(
  dailyData: DatedData<T>[],
  startDate: string,
  chunkSize: number,
  dataTransformer: (data: T[], startDate: string, endDate: string) => T,
) {
  const sortedDailyData = sortBy(dailyData, (d) => getDate(d.date));

  while (
    sortedDailyData.length > 0 &&
    getDate(sortedDailyData[0].date) < getDate(startDate)
  ) {
    sortedDailyData.shift();
  }

  if (sortedDailyData.length === 0) {
    return [];
  }

  const chunkedDailyData: DatedData<T>[][] = [[]];

  let currentChunkDays = new Set(
    getAllDaysInDateRange({
      start_date: startDate,
      end_date: addDaysToIsoDate(startDate, chunkSize - 1),
    }),
  );

  for (let i = 0; i < sortedDailyData.length; i++) {
    const datum = sortedDailyData[i];
    if (currentChunkDays.has(datum.date)) {
      chunkedDailyData[chunkedDailyData.length - 1].push(datum);
    } else {
      currentChunkDays = new Set(
        getAllDaysInDateRange({
          start_date: datum.date,
          end_date: addDaysToIsoDate(datum.date, chunkSize - 1),
        }),
      );
      chunkedDailyData.push([datum]);
    }
  }

  return chunkedDailyData.map((dayDataChunk) => {
    const chunkStartDate = dayDataChunk[0].date;
    const chunkEndDate = dayDataChunk[dayDataChunk.length - 1].date;
    return {
      date: chunkStartDate,
      data: dataTransformer(
        dayDataChunk.map((datum) => datum.data),
        chunkStartDate,
        chunkEndDate,
      ),
    };
  });
}

export function dailyToWeeklyData<T>(
  dailyData: DatedData<T>[],
  dataTransformer: (data: T[], startDate: string, endDate: string) => T,
) {
  if (dailyData.length === 0) {
    return [];
  }
  return dailyToRangedData(dailyData, dailyData[0].date, 7, dataTransformer);
}
